import { useState } from "react";
import { Box, Chip, Typography } from "@app/ui-kit";
import Popover from "@mui/material/Popover";
import { useDd } from "../lib/dd";

/** VOC-12's rule, carried: the one way an identifier is rendered. Label first, a quiet code chip second, the definition one interaction away. */
export type RefKind = "stage" | "gate" | "term" | "confidentiality" | "access";

export const Ref = ({ kind, id, label }: { kind: RefKind; id: string; label?: string }) => {
  const { content } = useDd();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const bucket = kind === "stage" ? content.stages : kind === "gate" ? content.gates : kind === "confidentiality" ? content.confidentiality : kind === "access" ? content.access : content.terms;
  const entry = bucket.find((e) => e.id === id);
  const shown = label ?? entry?.label ?? id;
  const plain = id.replace(/[_-]+/g, " ").toLowerCase();
  // 3.1: the chip carries the code only when the words shown do not already say it ("What a condition is" names the term; "condition" again beside it said it twice).
  const sameWord = plain === shown.toLowerCase() || plain === (entry?.label ?? "").toLowerCase() || new RegExp(`(^|[^a-z])${plain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z]|$)`).test(shown.toLowerCase());
  if (!entry) return <Typography component="span" variant="body2">{shown}</Typography>;
  return (
    <>
      <Box component="button" type="button" onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)} aria-label={`${shown}: what this means`}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, border: 0, background: "none", p: 0, cursor: "pointer", font: "inherit", color: "inherit" }}>
        <Typography component="span" variant="body2">{shown}</Typography>
        {!sameWord && <Chip label={id} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, color: "text.secondary", "& .MuiChip-label": { px: 0.75, textTransform: "none", letterSpacing: 0 } }} />}
      </Box>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box sx={{ p: 2, maxWidth: 420 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{entry.label}</Typography>
          <Typography variant="body2" color="text.secondary">{entry.description}</Typography>
          {entry.detail && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{entry.detail}</Typography>}
        </Box>
      </Popover>
    </>
  );
};
